import {Component, OnDestroy, OnInit} from '@angular/core';
import {UserSubscription} from "../../../../user/user-subscription";
import {ActivatedRoute} from "@angular/router";
import {distinctUntilChanged, filter, switchMap, takeUntil} from "rxjs/operators";
import {TypeUtils} from "../../../../shared/utils/type-utils";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../store";
import {IBoosterStats} from "../../../../store/reducers/booster.reducer";
import {Subject} from "rxjs";
import {Website} from "../../website";
import {combineLatest} from "rxjs";
import {Product} from "../../product";

@Component({
    selector: 'db-booster-image-optimizer',
    templateUrl: './booster-image-optimizer.component.html',
    styleUrls: ['./booster-image-optimizer.component.scss'],
    standalone: false
})
export class BoosterImageOptimizerComponent implements OnInit, OnDestroy {

  public currentSubscription: UserSubscription;
  public websiteId: number;
  public website: Website;
  public pageCount: string;
  public stats: IBoosterStats;
  public notConnected: boolean;
  public boosterPluginIsActive: boolean;
  public imageOptimizerPluginIsActive: boolean;
  public boosterUpdateAvailable: boolean;

  private readonly _destroyStream$ = new Subject<boolean>();

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _store: Store<fromStore.DbStoreState>,
  ) {
  }

  ngOnInit() {
    this._initData();
    if (!TypeUtils.isNull(this._route.snapshot.data['boosterTroubleshootData'])) {
      const disconnected = this._route.snapshot.data['boosterTroubleshootData'] === 'alive_but_not_connected';
      const restNoRoute = this._route.snapshot.data['boosterTroubleshootData'] === 'error';
      this.notConnected = disconnected || restNoRoute;
    }
    this._getPageCount();
    this._getWebsiteBoosterStats();
    this._checkBoosterPluginUpdateAvailable();
  }

  ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _initData(): void {
    this.website = this._route.snapshot.parent.parent.data['website'];
    if (this.website) {
      this._getWebsite();
      this._store.dispatch(new fromStore.LoadTroubleShootData({websiteId: this.website?.id, websiteUrl: this.website?.site_url}));
      this._getImageOptimizerAndBoosterPlugin();
    }
    this._getUserSubscriptionData();
  }


  private _getWebsite(): void {
    this._store.select(fromStore.getWebsitesLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() =>
          this._store.select(fromStore.getWebsite(this.website?.id))
        ),
        takeUntil(this._destroyStream$)
      )
      .subscribe((website) => {
        this.website = website;
        this.websiteId = website.id;
      });
  }

  private _getUserSubscriptionData(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() =>
          this._store.select(fromStore.getUserSubscription)
        ),
        takeUntil(this._destroyStream$)
      )
      .subscribe((userSubscription) => {
        this.currentSubscription = userSubscription;
      });
  }

  private _getPageCount(): void {
    this._store.select(fromStore.getWebsiteBoosterPagesCount(this.websiteId))
      .pipe(
        filter(pageCount => !!pageCount)
      )
      .subscribe(pageCount => {
        this.pageCount = TypeUtils.isDefined(pageCount) ? pageCount : null;
      });
  }

  private _getWebsiteBoosterStats(): void {
    this._store.select(fromStore.getWebsiteBoosterStats(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((stats): void => {
        if (!TypeUtils.isNull(stats)) {
          this.stats = stats;
        }
      });
  }

  private _getImageOptimizerAndBoosterPlugin(): void {
    this._store.select(fromStore.getWebsitePluginsLoaded(this.website?.id))
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsiteImageOptimizerPlugin(this.website?.id)),
          this._store.select(fromStore.getWebsiteOptimizerPlugin(this.website?.id))
        )),
        distinctUntilChanged((productOld: Product, productNew: any) => productOld.active === productNew.active),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([imageOptimizerPlugin, boosterPlugin]) => {
        this.imageOptimizerPluginIsActive = imageOptimizerPlugin?.active;
        this.boosterPluginIsActive = boosterPlugin?.active;
      });
  }

  private _checkBoosterPluginUpdateAvailable(): void {
    this._store.select(fromStore.getBoosterPluginInfo(this.website?.id))
      .pipe(
        filter(pluginInfo => !!pluginInfo)
      )
      .subscribe(([pluginUpdateAvailable, currentVersion]) => {
        if (((!TypeUtils.isNull(pluginUpdateAvailable) && !!pluginUpdateAvailable) || (TypeUtils.isNull(pluginUpdateAvailable) && TypeUtils.isNull(currentVersion)))) {
          this.boosterUpdateAvailable = true;
        }
      });
  }

}
